import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Code, Server, Database, Cpu, Globe } from "lucide-react";
import { GithubIcon, LinkedinIcon } from "../common/icons";
import INFO from "../../data/user";
import "./styles/hero.css";

const concepts = [
	"backend systems",
	"full-stack products",
	"RESTful microservices",
	"AI-assisted software"
];

const Hero = () => {
	const [conceptIndex, setConceptIndex] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setConceptIndex((prev) => (prev + 1) % concepts.length);
		}, 2800);
		return () => clearInterval(interval);
	}, []);

	return (
		<section className="hero-section">
			<div className="hero-status">
				<span className="status-dot"></span>
				<span>{INFO.homepage.status}</span>
			</div>

			<div className="hero-grid">
				<div className="hero-left">
					<h1 className="hero-headline">
						Building software for{" "}
						<span className="gradient-text">
							<AnimatePresence mode="wait">
								<motion.span
									key={concepts[conceptIndex]}
									initial={{ opacity: 0, y: 12, filter: "blur(4px)" }}
									animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
									exit={{ opacity: 0, y: -12, filter: "blur(4px)" }}
									transition={{ duration: 0.4 }}
									style={{ display: "inline-block" }}
								>
									{concepts[conceptIndex]}
								</motion.span>
							</AnimatePresence>
						</span>
					</h1>

					<p className="hero-description">
						Software Engineer at <strong>TCS</strong> focused on <strong>Java, Spring Boot</strong>, 
						backend engineering, and full-stack development. I build reliable applications, 
						optimize database latency, and explore scalable cloud architectures.
					</p>

					<div className="hero-actions">
						<Link to="/projects" className="btn-primary">
							<span>View Projects</span>
							<ArrowRight size={16} />
						</Link>
						<a 
							href={INFO.socials.github} 
							target="_blank" 
							rel="noreferrer" 
							className="btn-secondary"
						>
							<GithubIcon size={16} />
							<span>GitHub</span>
						</a>
					</div>

					<div className="hero-socials">
						<a 
							href={INFO.socials.linkedin} 
							target="_blank" 
							rel="noreferrer" 
							className="hero-social-pill"
						>
							<LinkedinIcon size={14} />
							<span>LinkedIn</span>
						</a>
						<a 
							href={INFO.socials.leetcode} 
							target="_blank" 
							rel="noreferrer" 
							className="hero-social-pill"
						>
							<Code size={14} />
							<span>LeetCode (1813 Peak)</span>
						</a>
					</div>
				</div>

				<div className="hero-right">
					<div className="architecture-card">
						<div className="arch-header">
							<span className="arch-title">
								<Cpu size={14} />
								SYSTEM FLOW OVERVIEW
							</span>
							<span className="tech-pill">ACTIVE NODE</span>
						</div>

						<div className="arch-flow">
							<div className="arch-node">
								<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<Globe size={14} color="#06b6d4" />
									<span className="arch-node-name">Client Layer</span>
								</div>
								<span className="arch-node-tech">React SPA</span>
							</div>

							<div className="arch-arrow">↓</div>

							<div className="arch-node active">
								<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<Server size={14} color="#10b981" />
									<span className="arch-node-name">REST API Gateway</span>
								</div>
								<span className="arch-node-tech">Spring Boot / Java</span>
							</div>

							<div className="arch-arrow">↓</div>

							<div className="arch-node">
								<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<Database size={14} color="#6366f1" />
									<span className="arch-node-name">Relational Engine</span>
								</div>
								<span className="arch-node-tech">PostgreSQL (~25% Optimized)</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Hero;
